import type { GooglePayButtonHost, GooglePayTarget } from '../interfaces/paymentInit.interface';
import { usePaymentInit } from './usePaymentInit';
import { usePaymentIntentCache } from './usePaymentIntentCache';
import { PAYMENT_INIT_ACTION, PAYMENT_SCRIPT_TIMEOUT_MS } from '../common/constants/paymentInit';
import { PaymentContractError } from '../utils/paymentContractError';

export function useGooglePayButton() {
  const API = useKegelApi();
  const gatewayStore = useKegelGatewayStore();

  const intentCache = usePaymentIntentCache('google_pay');

  let target: GooglePayTarget | null = null;

  const { isFailed: isGooglePayFailed, load } = usePaymentInit({
    action: PAYMENT_INIT_ACTION.GOOGLE_PAY,
    attemptTimeoutMs: PAYMENT_SCRIPT_TIMEOUT_MS,
    requestKey: () => String(target?.amount ?? ''),
    initialize: async (isCurrent) => {
      const request = target;
      if (!request) return;

      const intent = await intentCache.ensure(request.amount, async () => {
        const response = await API.creditCardInit(request.amount, 'google_pay');

        if (!response?.payment_intent) {
          throw new PaymentContractError(`Google Pay intent is missing in the init response for ${request.amount}`);
        }

        return response.payment_intent;
      });

      const latest = target;

      if (!isCurrent() || latest?.amount !== request.amount) return;

      await latest.container.mountGooglePayButton(
        intent,
        request.amount,
        () => isCurrent() && target?.amount === request.amount,
      );
    },
  });

  async function loadGooglePay(next: { amount: number, container?: GooglePayButtonHost }): Promise<void> {
    if (gatewayStore.provider === 'round' || !next.container) return;

    target = { amount: next.amount, container: next.container };
    await load();
  }

  return { isGooglePayFailed, loadGooglePay };
}
